import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
// Import Shared Components
import { DatePicker, CSVButton } from 'shared-components';
// Import Shared Utils
import {
  getEndOfDay,
  subtractMonth,
  currentDateTime,
  getStartAndEndDates,
} from 'shared-components/src/utils/dates';
import { formatDateForTransactionQuery } from 'shared-components/src/utils/formatDateTime';
// Import Global Component
import Title from '../../../../../../components/Title/index';
// Import Local Components
import TransfersTable from './components/TransfersTable';
import DateRange from '../DateRange';
// Import Component Styles
import {
  TransactionsHeader,
  DatesContainer,
  ExportsContainer,
} from '../../../../../Home/styles';
import { DropdownContainer, StyledDropdown } from './styles';
// Import transaction CSV helper functions
import { csvHeaders, getTransfersCSVData } from './csv';
import Label from '../../../../../../components/Label/index';

const Transfers = ({ setShowCancelModal, setCancelParams }) => {
  // Set data States
  const [dateRange, setDateRange] = useState(1);
  const [startDate, setStartDate] = useState(
    subtractMonth(currentDateTime(), 1)
  );
  const [endDate, setEndDate] = useState(getEndOfDay());
  const [focusedInput, setFocusedInput] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const statusOptions = [
    { key: '', val: 'All' },
    { key: 'awaiting_settlement', val: 'Requested' },
    { key: 'approved', val: 'Successful' },
    { key: 'processing', val: 'Pending' },
    { key: 'error', val: 'Failed' },
    { key: 'declined', val: 'Declined' },
    { key: 'cancelled', val: 'Canceled' },
  ];

  const intl = useIntl();

  const createIntl = key =>
    intl.formatMessage({
      id: `date-${key}-label`,
      defaultMessage: intl.messages[`date-${key}-label`],
      description: `Date ${key} label`,
    });

  const handleCsvData = data => {
    setCsvData(data);
  };

  const page = 'person-to-external';

  return (
    <div>
      <TransactionsHeader>
        <Title
          title={intl.messages[`${page}-home-transfers-title`]}
          titleFor="transfers"
          page="home"
        />
        <DatesContainer>
          <DateRange
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </DatesContainer>
        <ExportsContainer>
          <Label labelFor="export" page="home" label="Export to:" />
          <CSVButton
            data={getTransfersCSVData(csvData, intl.locale)}
            // Forces react-CSV component unmount and remount whenever transaction updates
            // Due to a bug in react-CSV
            key={csvData.map((trans, index) => index)}
            headers={csvHeaders}
            filename={`transfers-${startDate.toLocaleDateString()}-${endDate.toLocaleDateString()}`}
          >
            CSV
          </CSVButton>
        </ExportsContainer>
        <DropdownContainer>
          <Label labelFor="dropdown" page="home" label="Select status:" />
          <StyledDropdown
            options={statusOptions}
            selected={selectedStatus}
            updateItem={val => setSelectedStatus(val)}
          />
        </DropdownContainer>
        <DatesContainer>
          <DatePicker
            toPlaceholder="MM/DD/YYYY"
            fromPlaceholder="MM/DD/YYYY"
            startDate={startDate}
            endDate={endDate}
            onDatesChange={({ startDate, endDate }) => {
              setDateRange(null);
              const { validStartDate, validEndDate } = getStartAndEndDates(
                startDate,
                endDate
              );
              setStartDate(validStartDate);
              setEndDate(validEndDate);
            }}
            focusedInput={focusedInput}
            onFocusChange={focusedInput => {
              setFocusedInput(focusedInput);
            }}
            isOutsideRange={() => false}
            showDefaultInputIcon
            toText={createIntl('to')}
            fromText={createIntl('from')}
            label="true"
          />
        </DatesContainer>
      </TransactionsHeader>
      <TransfersTable
        startDate={formatDateForTransactionQuery(startDate)}
        endDate={formatDateForTransactionQuery(endDate)}
        handleCsvData={handleCsvData}
        selectedStatus={selectedStatus}
        setShowCancelModal={setShowCancelModal}
        setCancelParams={setCancelParams}
      />
    </div>
  );
};

Transfers.propTypes = {
  setShowCancelModal: PropTypes.func.isRequired,
  setCancelParams: PropTypes.func.isRequired,
};

export default Transfers;
